"use client"

// Message / MessageContent: role-aware presentation of a single turn.
// Adapted from Vercel AI Elements (https://ai-sdk.dev/elements, message),
// re-authored token-driven. User turns render as a navy brand bubble; assistant
// turns render flush (no bubble) so markdown, tools, and artifacts read as a
// document.

import { cn } from "@/lib/utils"
import type { ComponentProps, ReactNode } from "react"

export type MessageRole = "user" | "assistant"

/**
 * Row wrapper for one message; aligns user right, assistant left.
 *
 * @param from - the author of the message ("user" | "assistant").
 * @param className - extra classes merged onto the row.
 */
export function Message({
  from,
  className,
  ...props
}: ComponentProps<"div"> & { from: MessageRole }): ReactNode {
  return (
    <div
      data-role={from}
      className={cn("flex w-full", from === "user" ? "justify-end" : "justify-start", className)}
      {...props}
    />
  )
}

/**
 * The visible body of a message. Styling differs by role via the parent's
 * `data-role` so callers don't repeat the branch.
 *
 * @param from - the author of the message, to pick bubble vs flush styling.
 * @param className - extra classes merged onto the body.
 */
export function MessageContent({
  from,
  className,
  ...props
}: ComponentProps<"div"> & { from: MessageRole }): ReactNode {
  return (
    <div
      className={cn(
        "min-w-0 text-sm",
        from === "user"
          ? "max-w-[80%] whitespace-pre-wrap break-words rounded-2xl rounded-br-sm border border-user-bubble-border bg-user-bubble px-4 py-2.5 text-user-bubble-foreground"
          : "w-full max-w-full text-foreground",
        className
      )}
      {...props}
    />
  )
}
